"use client";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { css } from "styled-system/css";
import { Share, SquarePlus, Copy, Check, TriangleAlert } from "lucide-react";
import { detectIsIosSafari } from "@/lib/pwa-platform";

const SANS = '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';
const DISPLAY = '"Spectral", ui-serif, Georgia, serif';
const EASE = "cubic-bezier(0.23, 1, 0.32, 1)";

const STEPS = [
  "Abrí esta página en Safari",
  "Tocá el ícono de compartir (□↑) en la barra inferior",
  'Deslizá y tocá "Agregar a pantalla de inicio"',
  'Tocá "Agregar"',
];

export function InstallIosPage() {
  const [isSafari, setIsSafari] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setIsSafari(detectIsIosSafari());
  }, []);

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const safariSchemeUrl = currentUrl.replace(/^https?:/, "x-safari-https:");

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API no disponible (ej. contexto no seguro); el link visible alcanza para copiar a mano.
    }
  }

  return (
    <div
      style={{ fontFamily: SANS }}
      className={css({ minH: "100dvh", bg: "#f8fafc", display: "flex", flexDir: "column" })}
    >
      <header className={css({ px: "5", py: "5", display: "flex", justifyContent: "center" })}>
        <Link
          to="/"
          className={css({ display: "flex", alignItems: "center", gap: "2", textDecoration: "none" })}
        >
          <img src="/logo-mark.png" alt="" width={24} height={24} style={{ display: "block" }} />
          <span className={css({ fontWeight: "bold", fontSize: "md", color: "#0f172a" })}>SFTURNOS</span>
        </Link>
      </header>

      <main className={css({ flex: "1", maxW: "480px", mx: "auto", w: "100%", px: "5", pb: "12" })}>
        {!isSafari && (
          <div
            className={css({
              display: "flex", gap: "3", alignItems: "flex-start",
              bg: "#fffbeb", borderWidth: "1px", borderColor: "#fcd34d", rounded: "xl",
              p: "4", mb: "6",
            })}
          >
            <TriangleAlert size={20} strokeWidth={2} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
            <div>
              <p className={css({ fontWeight: "bold", fontSize: "sm", color: "#92400e", m: 0 })}>
                Necesitás abrir esta página en Safari
              </p>
              <p className={css({ mt: "1", fontSize: "sm", color: "#92400e", lineHeight: "1.5", m: 0 })}>
                En iPhone y iPad, solo Safari permite agregar la app a la pantalla de inicio.
              </p>
              <div className={css({ mt: "3", display: "flex", flexDir: "column", gap: "2" })}>
                <a
                  href={safariSchemeUrl}
                  className={css({
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "2",
                    py: "2.5", rounded: "lg", fontSize: "sm", fontWeight: "bold",
                    color: "white", bg: "#0f172a", textDecoration: "none",
                    transition: `transform 0.16s ${EASE}`, _active: { transform: "scale(0.97)" },
                  })}
                >
                  Abrir en Safari
                </a>
                <button
                  type="button"
                  onClick={() => void handleCopyLink()}
                  className={css({
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "2",
                    py: "2.5", rounded: "lg", fontSize: "sm", fontWeight: "semibold",
                    color: "#92400e", bg: "white", borderWidth: "1px", borderColor: "#fcd34d",
                    cursor: "pointer", transition: `transform 0.16s ${EASE}`, _active: { transform: "scale(0.97)" },
                  })}
                >
                  {copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
                  {copied ? "Link copiado" : "Copiar link"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={css({ textAlign: "center" })}>
          <p className={css({ fontSize: "12px", fontWeight: "600", letterSpacing: "0.14em", textTransform: "uppercase", color: "#4f46e5", m: 0 })}>
            iPhone / iPad
          </p>
          <h1
            style={{ fontFamily: DISPLAY, fontWeight: 600, margin: "10px 0 0" }}
            className={css({ fontSize: "3xl", color: "#0f172a", letterSpacing: "-0.02em", lineHeight: "1.1" })}
          >
            Instalá SFTurnos en tu iPhone / iPad
          </h1>
          <p className={css({ mt: "3", fontSize: "sm", color: "#51607a", lineHeight: "1.6" })}>
            Agregala a tu pantalla de inicio y abrila como una app, sin pasar por el navegador.
          </p>
        </div>

        <div
          className={css({
            mt: "7", rounded: "2xl", overflow: "hidden", bg: "#0f172a",
            borderWidth: "1px", borderColor: "#e2e8f0", boxShadow: "0 20px 50px rgba(15,23,42,0.14)",
          })}
        >
          <img
            src="/tutorial.gif"
            alt="Tutorial animado: tocar el ícono de compartir en Safari y elegir agregar a pantalla de inicio"
            className={css({ display: "block", w: "100%", h: "auto" })}
          />
        </div>

        <ol className={css({ mt: "8", display: "flex", flexDir: "column", gap: "2.5", listStyle: "none", p: 0, m: 0 })}>
          {STEPS.map((step, i) => (
            <li
              key={step}
              className={css({
                display: "flex", alignItems: "center", gap: "3.5", bg: "white",
                borderWidth: "1px", borderColor: "#e2e8f0", rounded: "xl", p: "3.5",
              })}
            >
              <span
                className={css({
                  w: "8", h: "8", rounded: "full", bg: "#eef2ff", color: "#4338CA",
                  display: "grid", placeItems: "center", flexShrink: 0, fontWeight: "bold", fontSize: "sm",
                })}
              >
                {i + 1}
              </span>
              {i === 1 ? <Share size={18} strokeWidth={2} color="#334155" style={{ flexShrink: 0 }} aria-hidden /> : null}
              {i === 2 ? <SquarePlus size={18} strokeWidth={2} color="#334155" style={{ flexShrink: 0 }} aria-hidden /> : null}
              <p className={css({ fontSize: "sm", color: "#334155", lineHeight: "1.45", m: 0 })}>{step}</p>
            </li>
          ))}
        </ol>

        <Link
          to="/"
          className={css({
            display: "block", textAlign: "center", mt: "9", fontSize: "sm", fontWeight: "semibold",
            color: "#64748b", textDecoration: "none", _hover: { color: "#0f172a" },
          })}
        >
          ← Volver a SFTurnos
        </Link>
      </main>
    </div>
  );
}
